/**
 * Mobile-first physical stock count — one ingredient per submission.
 * Shared team sessions sync progress via Supabase Realtime.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Database } from '@cafe-pos/supabase';
import { fmtNum, toDatetimeLocalValue } from '../inventoryPeriod.js';
import {
  StockCountRevertModal,
  type StockCountRevertTarget,
} from '../components/StockCountRevertModal.js';
import { supabase } from '../supabase.js';
import { Check } from 'lucide-react';
import { Callout, PageHeader } from '../ui/index.js';

type IngredientRow = Database['public']['Tables']['ingredients']['Row'];
type SessionRow = Database['public']['Tables']['stock_count_sessions']['Row'];

type CountFilter = 'remaining' | 'done' | 'variance' | 'all';

type DoneEntry = {
  countedQty: number;
  systemQty: number;
  delta: number;
  savedAt: string;
  countedByName: string | null;
};

type SessionMeta = {
  id: string;
  status: SessionRow['status'];
  openedAt: string;
  openedByName: string | null;
};

type PostedVariance = {
  lineId: string;
  ingredientName: string;
  unit: string;
  countedAt: string;
  systemQty: number;
  countedQty: number;
  delta: number;
  revertedAt: string | null;
  currentStock: number;
};

const COUNTED_AT_KEY = 'stock-count-counted-at.v1';

function isSameLocalDay(isoOrLocal: string, ref: Date): boolean {
  const d = new Date(isoOrLocal);
  if (Number.isNaN(d.getTime())) return false;
  return (
    d.getFullYear() === ref.getFullYear() &&
    d.getMonth() === ref.getMonth() &&
    d.getDate() === ref.getDate()
  );
}

function loadCountedAt(): string {
  try {
    const stored = localStorage.getItem(COUNTED_AT_KEY);
    // Count time persists within the day only — avoid backdating new counts after overnight.
    if (stored && isSameLocalDay(stored, new Date())) return stored;
  } catch {
    /* ignore quota errors */
  }
  return toDatetimeLocalValue(new Date());
}

function nowCountedAtLocal(): string {
  return toDatetimeLocalValue(new Date());
}

function profileLabel(
  p: { full_name: string | null; username: string | null; email: string | null } | null | undefined,
): string | null {
  if (!p) return null;
  return p.full_name?.trim() || p.username?.trim() || p.email?.trim() || null;
}

async function loadProfileNames(ids: string[]): Promise<Map<string, string>> {
  const unique = [...new Set(ids.filter(Boolean))];
  if (!supabase || unique.length === 0) return new Map();
  const { data } = await supabase
    .from('profiles')
    .select('id, full_name, username, email')
    .in('id', unique);
  const map = new Map<string, string>();
  for (const p of data ?? []) {
    const label = profileLabel(p);
    if (label) map.set(p.id, label);
  }
  return map;
}

export function StockCountView({
  clientId,
  role,
  onNavigate,
}: {
  clientId: string | null;
  role?: string;
  onNavigate?: (view: string) => void;
}) {
  const { t } = useTranslation();
  const canRevertCounts = role === 'admin' || role === 'owner';
  const [ingredients, setIngredients] = useState<IngredientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [postingId, setPostingId] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<CountFilter>('remaining');
  const [showTime, setShowTime] = useState(false);
  const [countedAtLocal, setCountedAtLocal] = useState(loadCountedAt);
  const [qtyById, setQtyById] = useState<Record<string, string>>({});

  const [session, setSession] = useState<SessionMeta | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [sessionDone, setSessionDone] = useState<Record<string, DoneEntry>>({});
  const [openingSession, setOpeningSession] = useState(false);
  const [closingSession, setClosingSession] = useState(false);
  const [live, setLive] = useState(false);

  const [postedVariances, setPostedVariances] = useState<PostedVariance[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [revertTarget, setRevertTarget] = useState<StockCountRevertTarget | null>(null);
  const [revertNote, setRevertNote] = useState('');
  const [revertSaving, setRevertSaving] = useState(false);
  const [revertError, setRevertError] = useState<string | null>(null);

  const sessionOpen = session?.status === 'open';

  const loadSessionLines = useCallback(async (sessionId: string) => {
    if (!supabase) return;
    const { data: lines, error: err } = await supabase
      .from('stock_count_session_lines')
      .select('ingredient_id, system_qty, counted_qty, delta, counted_at, counted_by')
      .eq('session_id', sessionId);

    if (err) {
      setError(err.message);
      return;
    }

    const profileIds = (lines ?? []).map((l) => l.counted_by).filter(Boolean) as string[];
    const names = await loadProfileNames(profileIds);

    const done: Record<string, DoneEntry> = {};
    for (const line of lines ?? []) {
      done[line.ingredient_id] = {
        countedQty: Number(line.counted_qty),
        systemQty: Number(line.system_qty),
        delta: Number(line.delta),
        savedAt: line.counted_at,
        countedByName: line.counted_by ? (names.get(line.counted_by) ?? null) : null,
      };
    }
    setSessionDone(done);
  }, []);

  const loadOpenSession = useCallback(async () => {
    if (!supabase || !clientId) return;
    setSessionLoading(true);
    const { data: row, error: err } = await supabase
      .from('stock_count_sessions')
      .select('id, status, opened_at, opened_by')
      .eq('client_id', clientId)
      .eq('status', 'open')
      .maybeSingle();

    if (err) {
      setError(err.message);
      setSession(null);
      setSessionDone({});
      setSessionLoading(false);
      return;
    }

    if (!row) {
      setSession(null);
      setSessionDone({});
      setSessionLoading(false);
      return;
    }

    const openerNames = await loadProfileNames(row.opened_by ? [row.opened_by] : []);
    setSession({
      id: row.id,
      status: row.status,
      openedAt: row.opened_at,
      openedByName: row.opened_by ? (openerNames.get(row.opened_by) ?? null) : null,
    });
    await loadSessionLines(row.id);
    setSessionLoading(false);
  }, [clientId, loadSessionLines]);

  const loadOpenSessionRef = useRef(loadOpenSession);
  loadOpenSessionRef.current = loadOpenSession;
  const loadSessionLinesRef = useRef(loadSessionLines);
  loadSessionLinesRef.current = loadSessionLines;

  const loadIngredients = useCallback(async () => {
    if (!supabase || !clientId) return;
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('ingredients')
      .select('*')
      .eq('client_id', clientId)
      .order('name');
    if (err) {
      setError(err.message);
      setLoading(false);
      return;
    }
    setIngredients(data ?? []);
    setLoading(false);
  }, [clientId]);

  const loadPostedVariances = useCallback(async () => {
    if (!supabase || !clientId) return;
    setHistoryLoading(true);
    const { data: lines, error: err } = await supabase
      .from('stock_count_lines')
      .select('id, ingredient_id, system_qty, counted_qty, delta, reverted_at, stock_count_id')
      .eq('client_id', clientId)
      .order('id', { ascending: false })
      .limit(50);

    if (err) {
      setHistoryLoading(false);
      return;
    }

    const countIds = [...new Set((lines ?? []).map((l) => l.stock_count_id))];
    const { data: counts } =
      countIds.length > 0
        ? await supabase.from('stock_counts').select('id, counted_at, created_at').in('id', countIds)
        : { data: [] as { id: string; counted_at: string; created_at: string }[] };

    const countMap = new Map(
      (counts ?? []).map((c) => [c.id, { countedAt: c.counted_at, postedAt: c.created_at }]),
    );

    const ingIds = [...new Set((lines ?? []).map((l) => l.ingredient_id))];
    const { data: ings } =
      ingIds.length > 0
        ? await supabase.from('ingredients').select('id, name, unit, stock').in('id', ingIds)
        : { data: [] as IngredientRow[] };

    const ingMap = new Map((ings ?? []).map((i) => [i.id, i]));

    setPostedVariances(
      (lines ?? [])
        .map((line) => ({
          lineId: line.id,
          ingredientName: ingMap.get(line.ingredient_id)?.name ?? '—',
          unit: ingMap.get(line.ingredient_id)?.unit ?? '',
          countedAt: countMap.get(line.stock_count_id)?.postedAt ?? '',
          systemQty: Number(line.system_qty),
          countedQty: Number(line.counted_qty),
          delta: Number(line.delta),
          revertedAt: line.reverted_at,
          currentStock: Number(ingMap.get(line.ingredient_id)?.stock ?? 0),
        }))
        .sort((a, b) => b.countedAt.localeCompare(a.countedAt)),
    );
    setHistoryLoading(false);
  }, [clientId]);

  useEffect(() => {
    void loadIngredients();
    void loadOpenSession();
    if (canRevertCounts) void loadPostedVariances();
  }, [loadIngredients, loadOpenSession, loadPostedVariances, canRevertCounts]);

  useEffect(() => {
    localStorage.setItem(COUNTED_AT_KEY, countedAtLocal);
  }, [countedAtLocal]);

  useEffect(() => {
    if (!supabase || !session?.id || session.status !== 'open') {
      setLive(false);
      return;
    }
    const client = supabase;
    const sessionId = session.id;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const reloadLines = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void loadSessionLinesRef.current(sessionId), 400);
    };
    const channel = client
      .channel(`stock-count-${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'stock_count_session_lines',
          filter: `session_id=eq.${sessionId}`,
        },
        reloadLines,
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'stock_count_sessions',
          filter: `id=eq.${sessionId}`,
        },
        () => void loadOpenSessionRef.current(),
      )
      .subscribe((status) => setLive(status === 'SUBSCRIBED'));
    return () => {
      if (timer) clearTimeout(timer);
      void client.removeChannel(channel);
      setLive(false);
    };
  }, [session?.id, session?.status]);

  const doneCount = Object.keys(sessionDone).length;
  const totalCount = ingredients.length;
  const varianceCount = Object.values(sessionDone).filter((e) => e.delta !== 0).length;
  const progressPct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = q ? ingredients.filter((ing) => ing.name.toLowerCase().includes(q)) : ingredients;

    list = list.filter((ing) => {
      const done = sessionDone[ing.id];
      const raw = qtyById[ing.id]?.trim();
      const pendingQty = raw ? Number(raw) : null;
      const pendingDelta =
        pendingQty != null && !Number.isNaN(pendingQty)
          ? pendingQty - Number(ing.stock)
          : null;

      if (filter === 'remaining') return !done;
      if (filter === 'done') return !!done;
      if (filter === 'variance') {
        if (done) return done.delta !== 0;
        return pendingDelta != null && pendingDelta !== 0;
      }
      return true;
    });

    return [...list].sort((a, b) => {
      const aDone = !!sessionDone[a.id];
      const bDone = !!sessionDone[b.id];
      if (aDone !== bDone) return aDone ? 1 : -1;
      return a.name.localeCompare(b.name);
    });
  }, [ingredients, search, filter, sessionDone, qtyById]);

  function setQty(id: string, value: string) {
    setQtyById((prev) => ({ ...prev, [id]: value }));
    setSuccess(null);
  }

  async function startSession() {
    if (!supabase || !clientId) return;
    setOpeningSession(true);
    setError(null);
    const { error: err } = await supabase.rpc('ensure_open_stock_count_session', {
      p_client_id: clientId,
    });
    setOpeningSession(false);
    if (err) {
      setError(err.message);
      return;
    }
    const now = nowCountedAtLocal();
    setCountedAtLocal(now);
    localStorage.setItem(COUNTED_AT_KEY, now);
    await loadOpenSession();
  }

  async function closeSession() {
    if (!supabase || !session?.id) return;
    setClosingSession(true);
    setError(null);
    const { error: err } = await supabase.rpc('close_stock_count_session', {
      p_session_id: session.id,
    });
    setClosingSession(false);
    if (err) {
      setError(err.message);
      return;
    }
    setSuccess(t('stockCount.sessionClosedSuccess'));
    setSession(null);
    setSessionDone({});
  }

  async function recount(id: string) {
    if (!supabase || !session?.id) return;
    setError(null);
    const { error: err } = await supabase.rpc('remove_stock_count_session_line', {
      p_session_id: session.id,
      p_ingredient_id: id,
    });
    if (err) {
      setError(err.message);
      return;
    }
    setSessionDone((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setQtyById((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setSuccess(null);
    setFilter('remaining');
  }

  async function submitOne(ing: IngredientRow) {
    if (!supabase || !clientId || !session?.id || !sessionOpen) return;

    const raw = qtyById[ing.id]?.trim();
    if (!raw) {
      setError(t('stockCount.enterQty'));
      return;
    }
    const counted = Number(raw);
    if (Number.isNaN(counted) || counted < 0) {
      setError(t('stockCount.invalidQty', { name: ing.name }));
      return;
    }

    const countedAt = new Date(countedAtLocal);
    if (Number.isNaN(countedAt.getTime())) {
      setError(t('stockCount.invalidTime'));
      return;
    }
    if (countedAt.getTime() > Date.now() + 60_000) {
      setError(t('stockCount.futureTime'));
      return;
    }

    const system = Number(ing.stock);
    const delta = counted - system;

    setPostingId(ing.id);
    setError(null);
    setSuccess(null);

    const { error: rpcErr } = await supabase.rpc('post_stock_count', {
      p_client_id: clientId,
      p_counted_at: countedAt.toISOString(),
      p_lines: [{ ingredient_id: ing.id, counted_qty: counted }],
      p_session_id: session.id,
    });

    setPostingId(null);

    if (rpcErr) {
      setError(rpcErr.message);
      return;
    }

    setQtyById((prev) => {
      const next = { ...prev };
      delete next[ing.id];
      return next;
    });

    if (delta === 0) {
      setSuccess(t('stockCount.savedMatched', { name: ing.name }));
    } else {
      setSuccess(
        t('stockCount.savedVariance', {
          name: ing.name,
          delta: `${delta > 0 ? '+' : ''}${fmtNum(delta)}`,
          unit: ing.unit,
        }),
      );
    }

    void loadSessionLines(session.id);

    if (delta !== 0) {
      void loadIngredients();
      if (canRevertCounts) void loadPostedVariances();
    }
  }

  async function executeRevert() {
    if (!supabase || !revertTarget) return;
    setRevertSaving(true);
    setRevertError(null);
    const { error: err } = await supabase.rpc('revert_stock_count_line', {
      p_line_id: revertTarget.lineId,
      p_note: revertNote.trim() || null,
    });
    setRevertSaving(false);
    if (err) {
      setRevertError(err.message);
      return;
    }
    setRevertTarget(null);
    setRevertNote('');
    setSuccess(t('stockCount.revertSuccess'));
    void loadIngredients();
    void loadPostedVariances();
  }

  function fmtPostedAt(iso: string) {
    if (!iso) return '—';
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  }

  function fmtSessionOpened(iso: string) {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  }

  return (
    <section className="stock-count-page stock-count-page--single ui-stack">
      <PageHeader
        title={t('stockCount.title')}
        subtitle={t('stockCount.subtitleSingle')}
        actions={
          onNavigate ? (
            <button
              type="button"
              className="link stock-count-insights-link"
              onClick={() => {
                window.history.pushState(null, '', '#/inventory-insights?tab=loss');
                onNavigate('inventory-insights');
              }}
            >
              {t('stockCount.viewLossInsights')} →
            </button>
          ) : undefined
        }
      />

      {error && <Callout tone="danger">{error}</Callout>}
      {success && <div className="auth-notice">{success}</div>}

      {sessionLoading ? (
        <div className="stock-count-session-banner muted">{t('common.loading')}</div>
      ) : sessionOpen && session ? (
        <div className="stock-count-session-banner stock-count-session-banner--open">
          <div className="stock-count-session-banner-main">
            <span className="stock-count-session-title">{t('stockCount.sessionTeamCount')}</span>
            {live && (
              <span className="stock-count-live-dot" title={t('stockCount.sessionLive')}>
                ●
              </span>
            )}
            <span className="muted stock-count-session-meta">
              {t('stockCount.sessionOpenedBy', {
                name: session.openedByName ?? t('stockCount.sessionSomeone'),
                when: fmtSessionOpened(session.openedAt),
              })}
            </span>
          </div>
          <button
            type="button"
            className="btn-ghost btn-sm"
            disabled={closingSession}
            onClick={() => void closeSession()}
          >
            {closingSession ? '…' : t('stockCount.sessionClose')}
          </button>
        </div>
      ) : (
        <div className="stock-count-session-banner stock-count-session-banner--idle">
          <div>
            <div className="stock-count-session-title">{t('stockCount.sessionNoActive')}</div>
            <p className="muted stock-count-session-idle-hint">{t('stockCount.sessionNoActiveHint')}</p>
          </div>
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={openingSession}
            onClick={() => void startSession()}
          >
            {openingSession ? '…' : t('stockCount.sessionStart')}
          </button>
        </div>
      )}

      {!loading && sessionOpen && totalCount > 0 && (
        <div className="stock-count-progress">
          <div className="stock-count-progress-head">
            <span className="stock-count-progress-label">
              {t('stockCount.progress', { done: doneCount, total: totalCount })}
            </span>
            <span className="muted stock-count-progress-pct">{progressPct}%</span>
          </div>
          <div
            className="stock-count-progress-track"
            role="progressbar"
            aria-valuenow={progressPct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className="stock-count-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
          {varianceCount > 0 && (
            <span className="stock-count-variance-badge">
              {t('stockCount.varianceCount', { count: varianceCount })}
            </span>
          )}
        </div>
      )}

      {sessionOpen && (
        <div className="stock-count-toolbar">
          <input
            className="input stock-count-search"
            type="search"
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="segment-ctrl stock-count-filters">
            {(
              [
                ['remaining', t('stockCount.filter.remaining')],
                ['done', t('stockCount.filter.done')],
                ['variance', t('stockCount.filter.variance')],
                ['all', t('stockCount.filter.all')],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={filter === key ? 'active' : ''}
                onClick={() => setFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>
          <button type="button" className="btn-ghost btn-sm" onClick={() => setShowTime((v) => !v)}>
            {showTime ? t('stockCount.hideTime') : t('stockCount.changeTime')}
          </button>
          {showTime && (
            <label className="stock-count-time">
              <span>{t('stockCount.countedAt')}</span>
              <input
                className="input"
                type="datetime-local"
                value={countedAtLocal}
                onChange={(e) => setCountedAtLocal(e.target.value)}
              />
            </label>
          )}
          {!showTime && (
            <div className="stock-count-now muted">
              {t('stockCount.countedNow')}: {new Date(countedAtLocal).toLocaleString()}
            </div>
          )}
        </div>
      )}

      {loading && sessionOpen ? (
        <div className="stock-count-loading">{t('common.loading')}</div>
      ) : sessionOpen ? (
        <ul className="stock-count-list">
          {visible.map((ing) => {
            const done = sessionDone[ing.id];
            if (done) {
              return (
                <li key={ing.id} className="stock-count-card stock-count-card--done">
                  <div className="stock-count-card-top">
                    <div className="stock-count-name-row">
                      <span className="stock-count-done-check" aria-hidden>
                        <Check className="ui-i" />
                      </span>
                      <div className="stock-count-name">{ing.name}</div>
                    </div>
                    <div className="stock-count-done-summary">
                      {t('stockCount.counted')}: {fmtNum(done.countedQty)} {ing.unit}
                      {done.delta === 0 ? (
                        <span className="stock-count-matched"> · {t('stockCount.matched')}</span>
                      ) : (
                        <span className={`stock-count-delta ${done.delta < 0 ? 'neg' : 'pos'}`}>
                          {' '}
                          · {done.delta > 0 ? '+' : ''}
                          {fmtNum(done.delta)} {ing.unit}
                        </span>
                      )}
                    </div>
                    {done.countedByName && (
                      <div className="stock-count-system muted">
                        {t('stockCount.sessionCountedBy', { name: done.countedByName })}
                      </div>
                    )}
                    <div className="stock-count-system muted">
                      {t('stockCount.system')}: {fmtNum(Number(ing.stock))} {ing.unit}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-ghost btn-sm stock-count-recount"
                    onClick={() => void recount(ing.id)}
                  >
                    {t('stockCount.recount')}
                  </button>
                </li>
              );
            }

            const countedQty = qtyById[ing.id] ?? '';
            const hasQty = countedQty.trim() !== '';
            const counted = hasQty ? Number(countedQty) : null;
            const system = Number(ing.stock);
            const delta = counted != null && !Number.isNaN(counted) ? counted - system : null;
            const showVar = delta != null && delta !== 0;
            const saving = postingId === ing.id;

            return (
              <li
                key={ing.id}
                className={`stock-count-card${hasQty ? ' stock-count-card--pending' : ''}`}
              >
                <div className="stock-count-card-top">
                  <div className="stock-count-name">{ing.name}</div>
                  <div className="stock-count-system muted">
                    {t('stockCount.system')}: {fmtNum(system)} {ing.unit}
                  </div>
                </div>
                <div className="stock-count-card-inputs">
                  <label>
                    <span className="stock-count-qty-label">{t('stockCount.counted')}</span>
                    <input
                      className="input stock-count-qty-input"
                      type="number"
                      min="0"
                      step="any"
                      inputMode="decimal"
                      placeholder="—"
                      value={countedQty}
                      disabled={postingId !== null}
                      onChange={(e) => setQty(ing.id, e.target.value)}
                    />
                    <span className="stock-count-unit">{ing.unit}</span>
                  </label>
                  {showVar && (
                    <span className={`stock-count-delta ${delta! < 0 ? 'neg' : 'pos'}`}>
                      {delta! > 0 ? '+' : ''}
                      {fmtNum(delta!)} {ing.unit}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="btn-primary stock-count-save-one"
                  disabled={!hasQty || postingId !== null}
                  onClick={() => void submitOne(ing)}
                >
                  {saving ? '…' : t('stockCount.saveOne')}
                </button>
              </li>
            );
          })}
          {visible.length === 0 && (
            <li className="stock-count-empty muted">
              {filter === 'remaining' && doneCount === totalCount && totalCount > 0
                ? t('stockCount.allDone')
                : t('stockCount.noResults')}
            </li>
          )}
        </ul>
      ) : null}

      {canRevertCounts && (
        <div className="panel" style={{ marginTop: 24 }}>
          <div className="panel-header">{t('stockCount.postedVariances')}</div>
          <p className="muted" style={{ fontSize: 13, padding: '8px 16px 0', margin: 0 }}>
            {t('stockCount.postedVariancesHint')}
          </p>
          {historyLoading ? (
            <div className="stock-count-loading" style={{ padding: 16 }}>
              {t('common.loading')}
            </div>
          ) : postedVariances.length === 0 ? (
            <div className="muted" style={{ padding: 16 }}>
              {t('stockCount.noPostedVariances')}
            </div>
          ) : (
            <table className="table" style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th>{t('stockCount.colWhen')}</th>
                  <th>{t('stockCount.colIngredient')}</th>
                  <th style={{ textAlign: 'right' }}>{t('stockCount.colDelta')}</th>
                  <th>{t('stockCount.colDetail')}</th>
                  <th style={{ width: 88 }}></th>
                </tr>
              </thead>
              <tbody>
                {postedVariances.map((row) => (
                  <tr key={row.lineId} style={row.revertedAt ? { opacity: 0.55 } : undefined}>
                    <td className="muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                      {fmtPostedAt(row.countedAt)}
                    </td>
                    <td style={{ fontWeight: 500 }}>
                      {row.ingredientName}
                      <span className="muted" style={{ fontWeight: 400, marginLeft: 6, fontSize: 12 }}>
                        ({row.unit})
                      </span>
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        fontWeight: 600,
                        color:
                          row.delta < 0
                            ? 'var(--danger)'
                            : row.delta > 0
                              ? 'var(--success)'
                              : undefined,
                      }}
                    >
                      {row.delta > 0 ? '+' : ''}
                      {fmtNum(row.delta)} {row.unit}
                    </td>
                    <td className="muted" style={{ fontSize: 12 }}>
                      {row.revertedAt ? (
                        t('stockCount.reverted')
                      ) : (
                        <>
                          {t('stockCount.system')} {fmtNum(row.systemQty)} → {fmtNum(row.countedQty)}{' '}
                          {row.unit}
                        </>
                      )}
                    </td>
                    <td>
                      {!row.revertedAt && (
                        <button
                          type="button"
                          className="btn-ghost btn-sm"
                          onClick={() => {
                            setRevertTarget({
                              lineId: row.lineId,
                              ingredientName: row.ingredientName,
                              unit: row.unit,
                              countedAt: row.countedAt,
                              delta: row.delta,
                              currentStock: row.currentStock,
                            });
                            setRevertNote('');
                            setRevertError(null);
                          }}
                        >
                          {t('stockCount.revert')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {revertTarget && (
        <StockCountRevertModal
          target={revertTarget}
          note={revertNote}
          saving={revertSaving}
          error={revertError}
          onNoteChange={setRevertNote}
          onClose={() => !revertSaving && setRevertTarget(null)}
          onConfirm={() => void executeRevert()}
        />
      )}
    </section>
  );
}
